import * as Sentry from '@sentry/react';

/**
 * Dueño de los borradores de un asistente.
 *
 * Los asistentes guardan borradores en `sessionStorage` que no dicen de qué
 * entidad son. Un ámbito (`DraftScope`) añade una clave de dueño: al abrir el
 * asistente con `claimDraftScope`, si el dueño no es la entidad en pantalla se
 * descartan sus borradores y se fija el nuevo dueño.
 *
 * 🔴 Las carpetas de fotos de OPFS NO entran aquí (Ruling 5, 2026-11-02): son
 * de todo el origen y las comparten las pestañas, así que vaciarlas al cambiar
 * de entidad en una tira las fotos sin guardar de OTRA. La protección es por
 * LISTA: el formulario pinta y el guardado sube solo los nombres que lista su
 * borrador (claves de `sessionStorage`, que sí se descartan).
 */
export interface DraftScope {
  /** Nombre del ámbito (etiqueta de Sentry). */
  name: string;
  /** Clave donde se guarda el id del dueño. Debe empezar por new-/edit-/add-. */
  ownerKey: string;
  /** ¿Es esta clave un borrador del ámbito? (incluye claves con sufijo) */
  isDraftKey(key: string): boolean;
}

/** Almacén mínimo; `key`/`length` son opcionales (se cae a `Object.keys`). */
export type DraftStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> &
  Partial<Pick<Storage, 'key' | 'length'>>;

type ReadableDraftStore = Pick<Storage, 'getItem'>;

/**
 * El espejo duradero de la APK (`isDurableKey`) solo guarda claves que empiezan
 * por estos prefijos; un dueño fuera de ellos se perdería al reiniciar la APK.
 */
const DURABLE_KEY_PATTERN = /^(new|edit|add)-/;

export const defineDraftScope = (scope: DraftScope): DraftScope => {
  if (!DURABLE_KEY_PATTERN.test(scope.ownerKey)) {
    throw new Error(
      `DraftScope "${scope.name}": ownerKey "${scope.ownerKey}" debe empezar por new-, edit- o add- para entrar en el espejo duradero`
    );
  }
  return scope;
};

export class DraftOwnerMismatchError extends Error {
  scope: string;
  owner: string | null;
  entityId: string;

  constructor(scope: string, owner: string | null, entityId: string) {
    super(
      `Los borradores de "${scope}" son de ${owner ?? '(nadie)'} y se intentó usar ${entityId}`
    );
    this.name = 'DraftOwnerMismatchError';
    this.scope = scope;
    this.owner = owner;
    this.entityId = entityId;
  }
}

const listKeys = (store: DraftStore): string[] => {
  if (typeof store.key === 'function' && typeof store.length === 'number') {
    const keys: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key !== null) keys.push(key);
    }
    return keys;
  }
  return Object.keys(store);
};

/**
 * Reclama el ámbito para `entityId`: si los borradores eran de otra entidad (o
 * huérfanos) los descarta y fija el dueño. Idempotente. Sin `entityId` no hace
 * nada.
 *
 * 🔴 Nunca lanza (Ruling 19): es la primera línea de los loaders, y un
 * almacén bloqueado (modo privado, cuota, WebView sin permiso) no puede tumbar
 * la pantalla. El sentido seguro es «nunca dejar ver copias ajenas»:
 * - Si no se puede leer el dueño, se trata como ajeno (se intenta descartar).
 * - El dueño SOLO se apunta si se descartó todo lo ajeno. Si no se pudo
 *   listar o borrar alguna copia, el dueño se queda como estaba: apuntar
 *   `entityId` haría pasar las copias de otro por suyas (lectores que miran el
 *   dueño y la barrera del guardado, `assertDraftOwner`, las darían por
 *   buenas). Con el dueño anterior intacto la barrera sigue saltando y la
 *   próxima carga vuelve a intentarlo.
 */
export const claimDraftScope = (
  scope: DraftScope,
  entityId: string | null | undefined,
  store: DraftStore = sessionStorage
): { discarded: string[] } => {
  if (!entityId) return { discarded: [] };

  let owner: string | null = null;
  try {
    owner = store.getItem(scope.ownerKey);
  } catch {
    // No se puede leer: se trata como de otro (ver arriba).
  }
  if (owner === entityId) return { discarded: [] };

  // Se listan antes de borrar: borrar mientras se recorre desplaza los índices.
  let candidates: string[] = [];
  let complete = true;
  try {
    candidates = listKeys(store).filter(
      (key) => key !== scope.ownerKey && scope.isDraftKey(key)
    );
  } catch {
    complete = false;
  }
  const discarded: string[] = [];
  candidates.forEach((key) => {
    try {
      store.removeItem(key);
      discarded.push(key);
    } catch {
      complete = false;
    }
  });

  if (complete) {
    try {
      store.setItem(scope.ownerKey, entityId);
    } catch {
      /* almacén lleno o bloqueado: sin dueño, la próxima carga vuelve a descartar */
    }
  }

  return { discarded };
};

/** ¿Siguen los borradores siendo de `entityId`? */
export const isDraftOwner = (
  scope: DraftScope,
  entityId: string,
  store: ReadableDraftStore = sessionStorage
): boolean => store.getItem(scope.ownerKey) === entityId;

/**
 * Barrera del guardado: lanza si los borradores son de OTRA entidad. Una
 * reproducción de la cola offline (`isReplay`) o la ausencia de dueño pasan.
 */
export const assertDraftOwner = (
  scope: DraftScope,
  entityId: string | undefined,
  opts: { isReplay?: boolean },
  store: ReadableDraftStore = sessionStorage
): void => {
  if (opts.isReplay || !entityId) return;
  const owner = store.getItem(scope.ownerKey);
  if (!owner || owner === entityId) return;

  const error = new DraftOwnerMismatchError(scope.name, owner, entityId);
  Sentry.captureException(error, {
    tags: { guard: `draft-owner-mismatch:${scope.name}` },
  });
  throw error;
};
